import React, { useState } from 'react';

import styled from 'styled-components';
import { useI18n } from '@oneaudi/i18n-context';
import { SelectField } from '@oneaudi/unified-web-components';
import { SYS_TYPOGRAPHY_HEADLINE_ORDER4_NORMAL_BP2_XL } from '@oneaudi/unified-web-common';
import type { LanguageLink } from '../../types/audi-footer-response.types';
import type { TrackingClickEvent} from '../../utils/tracking';
import { useTracking } from '../../utils/tracking';
import { TrackingContext } from '../../context';

type LanguageSwitchProps = {
  links: LanguageLink[];
};


const LanguageSwitch: React.FC<LanguageSwitchProps> = ({ links }) => {
  const trackingService = React.useContext(TrackingContext);
  const { sendClickEvent } = useTracking(trackingService);

  // Find initial selected link object
  const initialSelected = links?.find((e) => e.IsSelected) ?? links?.[0];

  // Keep the selected URL as state
  const [selectedUrl, setSelectedUrl] = useState(initialSelected?.Url ?? "");

  const sendButtonClickEvent = (
    label: string,
    url: string,
    variant: string,
    openin: string,
    position: string
  ) => {
    const event: TrackingClickEvent = {
      linkLabel: label,
      linkUrl: url,
      elementName: variant,
      headline: label,
      OpenIn: openin,
      position,
    };
    sendClickEvent(event);
  };

  const onChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const newUrl = event.target.value;
    setSelectedUrl(newUrl);

    const selectedLink = links?.find((e) => e.Url === newUrl);
    if (selectedLink) {
      sendButtonClickEvent(
        selectedLink.Text,
        selectedLink.Url,
        "drop down",
        "language",
        ""
      );
      if (typeof window !== "undefined") {
        window.open(newUrl, "_self");
      }
    }
  };

  const selectAriaLabel = `${useI18n({
    defaultMessage: "Please select",
    id: "axs.ui.country-selector.country.select.placeholder",
  })} ${useI18n({
    defaultMessage: "country",
    id: "axs.ui.country-selector.country.select.label",
  })}`;

  return (
    <FooterLanguageSwitchWrapperStyled>
      <FooterLanguageSwitchStyled>
        <SelectField
          id="select-language"
          icon="web"
          value={selectedUrl}
          // aria-label={selectAriaLabel}
          label={selectAriaLabel}
          onChange={onChange}
          autoComplete="language"
        >
          {links?.map((link) => (
            <option key={`${link.Text}${link.Url}`} value={link.Url}>
              {link.Text}
            </option>
          ))}
        </SelectField>
      </FooterLanguageSwitchStyled>
    </FooterLanguageSwitchWrapperStyled>
  );
};

export default LanguageSwitch;

const FooterLanguageSwitchWrapperStyled = styled.div`
  position: relative;
`;

const FooterLanguageSwitchStyled = styled.div`
  align-items: center;
  color: var(--one-footer-color-white);
  display: flex;
  flex-wrap: nowrap;
  margin: 0;

  & > select {
    appearance: none;
    background-color: transparent;
    background-image: url("data:image/svg+xml;utf8,<svg height='24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5.5 5.5L18 10' color='white' fill='none' stroke='currentColor'></path></svg>");
    background-position: right 0px top -3px; /* Change the px value here to change the distance */
    background-repeat: no-repeat, repeat;
    background-size: auto;
    font-family: ${SYS_TYPOGRAPHY_HEADLINE_ORDER4_NORMAL_BP2_XL.fontFamily}, sans-serif;
    border: 0;
    color: var(--one-footer-color-white);
    height: auto;
    line-height: 1.8rem;
    padding: 0 24px 0 0;
    text-decoration: none;
    width: auto;
    border: none;

    &::-ms-expand {
      display: none;
    }

    &:hover {
      border: 0;
      color: var(--one-footer-color-white);
    }

    &:focus {
      color: var(--one-footer-color-white);
      outline: revert;
    }

    & > option {
      color: var(--one-footer-color-black);
      font-weight: normal;
      
    }
  }
  label {
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;